import type { Ref } from "vue";
import type { Requests } from "../common/request.common";
import type { CustomerRegistrationResponseTypes } from "../common/response.common";
import { asRemoveHook, asUpdateHook, get, post, postEmpty } from "./common";

export const customerRegistration = {
    record(data: typeof Requests.CustomerRegistration.Record.Input) {
        return post<typeof data, CustomerRegistrationResponseTypes.Record>(
            `/customer-registration/record`,
            data,
        );
    },
    create(data: typeof Requests.CustomerRegistration.Create.Input) {
        return post<typeof data>(`/customer-registration/create`, data);
    },
    signup(data: typeof Requests.CustomerRegistration.Signup.Input) {
        return post<typeof data>(`/customer-registration/signup`, data, { authenticated: false });
    },
    obtain(id: string) {
        return get<CustomerRegistrationResponseTypes.Obtain>(`/customer-registration/obtain/${id}`);
    },
    registrationSetup(
        id: string,
        data: typeof Requests.CustomerRegistration.RegistrationSetup.Input,
    ) {
        return post<typeof data>(`/customer-registration/registration-setup/${id}`, data);
    },
    advance(id: string) {
        return postEmpty(`/customer-registration/advance/${id}`);
    },
    surveyCreate(id: string, data: typeof Requests.CustomerRegistration.SurveyCreate.Input) {
        return post<typeof data>(`/customer-registration/survey-create/${id}`, data);
    },
    surveyDispatch(id: string) {
        return postEmpty(`/customer-registration/survey-dispatch/${id}`);
    },
    surveyReport(id: string, data: typeof Requests.CustomerRegistration.SurveyReport.Input) {
        return post<typeof data>(`/customer-registration/survey-report/${id}`, data);
    },
    customerCreate(id: string, data: typeof Requests.CustomerRegistration.CustomerCreate.Input) {
        return post<typeof data>(`/customer-registration/customer-create/${id}`, data);
    },
    productCreate(id: string, data: typeof Requests.Subscription.Create.Input) {
        return post<typeof data>(`/customer-registration/product-create/${id}`, data);
    },
    installationCreate(
        id: string,
        data: typeof Requests.CustomerRegistration.InstallationCreate.Input,
    ) {
        return post<typeof data>(`/customer-registration/installation-create/${id}`, data);
    },
    installationDispatch(id: string) {
        return postEmpty(`/customer-registration/installation-dispatch/${id}`);
    },
    provisionCreate(id: string, data: typeof Requests.CustomerRegistration.ProvisionCreate.Input) {
        return post<typeof data>(`/customer-registration/provision-create/${id}`, data);
    },
    provisionCancel(id: string, data: typeof Requests.CustomerRegistration.ProvisionCancel.Input) {
        return post<typeof data>(`/customer-registration/provision-cancel/${id}`, data);
    },
    installationFinish(id: string) {
        return postEmpty(`/customer-registration/installation-finish/${id}`);
    },
    installationReport(
        id: string,
        data: typeof Requests.CustomerRegistration.InstallationReport.Input,
    ) {
        return post<typeof data>(`/customer-registration/installation-report/${id}`, data);
    },
    provisionFinalize(
        id: string,
        data: typeof Requests.CustomerRegistration.ProvisionFinalize.Input,
    ) {
        return post<typeof data>(`/customer-registration/provision-finalize/${id}`, data);
    },
    finalizationCreate(
        id: string,
        data: typeof Requests.CustomerRegistration.FinalizationCreate.Input,
    ) {
        return post<typeof data>(`/customer-registration/finalization-create/${id}`, data);
    },
    finalizationFinish(
        id: string,
        data: typeof Requests.CustomerRegistration.FinalizationFinish.Input,
    ) {
        return post<typeof data>(`/customer-registration/finalization-finish/${id}`, data);
    },
    cancel(id: string) {
        return postEmpty(`/customer-registration/cancel/${id}`);
    },
    generateExport(data: typeof Requests.CustomerRegistration.GenerateExport.Input) {
        return post<typeof data>(`/customer-registration/generate-export`, data);
    },
    formHooks: {
        update(id: string, isLoading?: Ref<boolean>) {
            return async (data: typeof Requests.CustomerRegistration.Update.Input) => {
                return await asUpdateHook(
                    post<typeof data>(`/customer-registration/update/${id}`, data),
                    isLoading,
                );
            };
        },
        remove(id: string, isLoading?: Ref<boolean>) {
            return async (data: typeof Requests.CustomerRegistration.Remove.Input) => {
                return await asRemoveHook(
                    post<typeof data>(`/customer-registration/remove/${id}`, data),
                    isLoading,
                );
            };
        },
    },
    dev: {
        prune(id: string) {
            return postEmpty(`/dev/customer-registration/prune/${id}`);
        },
    },
};
